import {
  AddressType,
  useAutocompleteAddressInput,
} from "@faire/retailer-visitor-shared/components/AutocompleteAddressInput/AutocompleteAddressInputState";
import { CustomAddressDropdown } from "@faire/retailer-visitor-shared/components/GoogleMapsComponent/CustomAddressDropdown";
import { debounceIfPossible } from "@faire/web--source/common/globals/debounceIfPossible";
import { Input } from "@faire/web--source/slate/Input";
import { LoadingSpinner } from "@faire/web--source/slate/LoadingSpinner";
import * as React from "react";
import { useEffect, useMemo } from "react";
import styled from "styled-components";

interface IProps {
  type: AddressType;
  id: string;
  value?: string;
  label?: string;
  placeholder?: string;
  onChange: (value: string) => void;
  onSelect?: (placeId: string) => void;
  onCannotFindClick?: () => void;
  error?: string;
  prefixNode?: React.ReactNode;
}

/**
 * @deprecated Use FormikAddressSearch instead from retailer-visitor-shared/src/components/FormikAddressSearch/FormikAddressSearch.tsx
 */
export const AutocompleteAddressInput: React.FunctionComponent<IProps> = ({
  type,
  id,
  value,
  label,
  placeholder,
  onChange,
  onSelect,
  onCannotFindClick,
  error,
  prefixNode,
}) => {
  const {
    isDropdownVisible,
    fetchAutocompleteResults,
    autocompleteOptions,
    showDropdown,
    hideDropdown,
    handleOptionSelect,
    handleCannotFindClick,
    isLoading,
  } = useAutocompleteAddressInput(type, undefined, onChange, onCannotFindClick, onSelect);

  const debouncedFetchAutocomplete = useMemo(
    () =>
      debounceIfPossible((value) => {
        if (isDropdownVisible) {
          fetchAutocompleteResults(value);
        }
      }, 250),
    [fetchAutocompleteResults, isDropdownVisible]
  );

  useEffect(() => {
    debouncedFetchAutocomplete(value);
  }, [debouncedFetchAutocomplete, value]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    onChange(value);
    showDropdown();
  };

  return (
    <Wrapper>
      <CustomAddressDropdown
        open={isDropdownVisible}
        onOpen={showDropdown}
        onClose={hideDropdown}
        options={autocompleteOptions}
        onSelect={handleOptionSelect}
        onCannotFindClick={handleCannotFindClick}
        showCannotFindOption={!!onCannotFindClick}
      >
        <Input
          type="text"
          onChange={handleChange}
          label={label}
          value={value}
          placeholder={placeholder}
          data-test-id={id}
          error={error}
          prefixNode={prefixNode}
        />
        {isLoading ? (
          <LoadingWrapper>
            <LoadingSpinner size="medium" />
          </LoadingWrapper>
        ) : null}
      </CustomAddressDropdown>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  position: relative;
`;

const LoadingWrapper = styled.div`
  position: absolute;
  top: 22px;
  right: 8px;
  transform: translateY(-50%);
`;
